// -----------------------------------------------------------------Imports---
import {
  Button,
  Container,
  Dialog,
  Grid,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import moment from "moment";
import React, { Key, useMemo, useState } from "react";
import { useEffect } from "react";
import { toast } from "react-toastify";

import { Accordion, AccordionDetails, AccordionSummary } from "../../components/TableAccordions/TableAccordions";
import Transition from "../../components/Transition/Transition";
import { DeviceCategoryModel } from "../../models/DeviceCategoryModel";
import {
  DeviceFilter,
  DeviceGridKey,
  DeviceModel,
  DeviceState,
  DeviceTableKey,
  getDeviceStateName,
  gridKeys,
  tableKeys,
} from "../../models/DeviceModel";
import { DeviceTypeModel } from "../../models/DeviceTypeModel";
import ResponseModel from "../../models/ResponseModel";
import DeviceCategoryService from "../../services/deviceCategories/DeviceCategoryService";
import DeviceService from "../../services/devices/DeviceService";
import DeviceTypeService from "../../services/deviceTypes/DeviceTypeService";
import { getLongAddress, hasApiError } from "../../utils/Utils";

import { style } from "./Devices.style";
import ReturnDevice from "./ReturnDevice";

const showedStates: DeviceState[] = [
  { key: "S", desc: "Szervízben" },
  { key: "K", desc: "Kihelyezett" },
  { key: "V", desc: "Visszavett" },
  { key: "U", desc: "Raktáron" },
  { key: "O", desc: "Összerendelt" },
];

// -----------------------------------------------------------------Exports---
const Devices = (): JSX.Element => {
  const [devices, setDevices] = useState<DeviceModel[]>([]);
  const [filteredDevices, setFilteredDevices] = useState<DeviceModel[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState<number>(50);
  const [page, setPage] = useState<number>(0);
  const [isReturningOpen, setReturningOpen] = useState<boolean>(false);
  const [returningDevice, setReturningDevice] = useState<DeviceModel | undefined>(undefined);
  const [expanded, setExpanded] = useState<string | false>(false);
  const [categories, setCategories] = useState<DeviceCategoryModel[]>([]);
  const [filteredTypes, setFilteredTypes] = useState<DeviceTypeModel[]>([]);
  const [filter, setFilter] = useState<DeviceFilter>({
    name: "",
    serial: "",
    state: "K",
    category: "all",
    type: "all",
  });

  //render the head once
  const headMemo: JSX.Element[] = useMemo((): JSX.Element[] => {
    return tableKeys.map((tableKey: DeviceTableKey, i: Key): JSX.Element => {
      return (
        <TableCell key={i} style={style.tableHeadCell} align={tableKey.align ?? "left"}>
          {tableKey.label}
        </TableCell>
      );
    });
  }, []);

  // filter the devices when the filter changes
  useEffect((): void => {
    let _devices: DeviceModel[] = [...devices];
    _devices = _devices.filter((device: DeviceModel): boolean => {
      return (
        (filter.name.length < 3 ||
          (device.partner !== undefined && (device.partner ?? "").toLowerCase().includes(filter.name.toLowerCase()))) &&
        (filter.serial.length < 3 ||
          (device.serialNumber !== undefined &&
            device.serialNumber !== null &&
            device.serialNumber.toLowerCase().includes(filter.serial.toLowerCase()))) &&
        (filter.state === "all" || device.state === filter.state) &&
        (filter.category === "all" || device.deviceCategoryId === parseInt(filter.category)) &&
        (filter.type === "all" || device.deviceTypeId === parseInt(filter.type))
      );
    });

    setFilteredDevices(_devices);
  }, [filter]);

  // getting all the categories
  const getCategories = async (): Promise<void> => {
    const response: ResponseModel = await DeviceCategoryService.getDeviceCategories();
    if (hasApiError(response)) return;
    else {
      setCategories(response.data as DeviceCategoryModel[]);
    }
  };

  // get the devices
  const getDevices = async (): Promise<void> => {
    const response: ResponseModel = await DeviceService.getDevices({});
    let data: DeviceModel[];
    if (hasApiError(response)) return;
    else {
      data = response.data as DeviceModel[];
      data.forEach((device: DeviceModel): void => {
        device.partner = device.deviceRental?.deliveryAddress?.partner?.name;
        device.partnerAddressName = device.deviceRental?.deliveryAddress?.name;
        if (device.deviceRental?.deliveryAddress) device.partnerFullAddress = getLongAddress(device.deviceRental.deliveryAddress);
        device.deviceTypeName = device.deviceType?.name;
        device.deviceCategoryName = device.deviceCategory?.name;
        device.statusName = getDeviceStateName(device.state ?? "");
        const rentalContract = device.deviceRental?.deviceContract;
        if (rentalContract) {
          if (moment(rentalContract.contractStart).year() >= 3000) device.outDate = null;
          else device.outDate = moment(rentalContract.contractStart).format("YYYY-MM-DD");
          if (moment(rentalContract.contractEnd).year() >= 3000) device.inDate = null;
          else device.inDate = moment(rentalContract.contractEnd).format("YYYY-MM-DD");
        } else {
          device.outDate = null;
          device.inDate = null;
        }
        device.fee = device.deviceRental?.rentalFee ?? device.deviceType?.rentalFee;
      });
      data = data.filter((device: DeviceModel): boolean => {
        return showedStates.map((state: DeviceState): string => state.key).includes(device.state ?? "");
      });
      setDevices(data);
      setFilter({ ...filter }); //to trigger the filtering
    }
  };

  //paging
  const handleChangePage = (_event: unknown, newPage: number): void => {
    setPage(newPage);
  };

  //changing row amount
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
  };

  //change filter
  const onFilterChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>,
    key: string,
  ): void => {
    const newFilter: DeviceFilter = { ...filter };
    (newFilter as Record<string, unknown>)[key] = event.target.value;

    // when category changes, reset type filter and load types for that category
    if (key === "category") {
      newFilter.type = "all";
      if (event.target.value !== "all") {
        void loadTypesForCategory(parseInt(event.target.value));
      } else {
        setFilteredTypes([]);
      }
    }

    setFilter(newFilter);
  };

  // load types for a selected category (for filter dropdown)
  const loadTypesForCategory = async (categoryCode: number): Promise<void> => {
    const response: ResponseModel = await DeviceTypeService.getDeviceTypes({ id: categoryCode });
    if (hasApiError(response)) return;
    setFilteredTypes(response.data as DeviceTypeModel[]);
  };

  //get the data on load
  useEffect((): void => {
    void getDevices();
    void getCategories();
  }, []);

  // take back request
  const onTakeBack = async (device: DeviceModel): Promise<void> => {
    const loadingToastId = toast.loading("Visszavétel folyamatban...");
    const response: ResponseModel = await DeviceService.deviceTakeBack(device.id);
    if (hasApiError(response)) {
      toast.dismiss(loadingToastId);
      return;
    } else {
      toast.update(loadingToastId, { render: "Visszavétel sikeresen rögzítve", type: "success", isLoading: false, autoClose: 4000 });
      void getDevices();
    }
  };

  // replacement request
  const onReplacement = async (device: DeviceModel): Promise<void> => {
    const loadingToastId = toast.loading("Csereigény leadása folyamatban...");
    const response: ResponseModel = await DeviceService.deviceReplacement(device.id);
    if (hasApiError(response)) {
      toast.dismiss(loadingToastId);
      return;
    } else {
      toast.update(loadingToastId, { render: "Csereigény sikeresen leadva", type: "success", isLoading: false, autoClose: 4000 });
      void getDevices();
    }
  };

  //accordion handling
  const accordionChange =
    (panel: string): (() => void) =>
      (): void => {
        if (expanded === panel) setExpanded(false);
        else setExpanded(panel);
      };

  // --------------------------------------------------------------Return---
  return (
    <Paper style={style.paper}>
      {/* Header */}
      <Typography variant="h3" style={style.title}>
        Eszközök listája
      </Typography>
      <Container style={style.container}>
        <TextField
          variant="outlined"
          value={filter.name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => onFilterChange(e, "name")}
          style={style.filterTextField}
          label="Ügyfél"
        />
        <TextField
          variant="outlined"
          value={filter.serial}
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => onFilterChange(e, "serial")}
          style={style.filterTextField}
          label="Gyáriszám"
        />
        <Select
          value={filter.state.toString()}
          style={style.filterTextField}
          onChange={(e: SelectChangeEvent<string>): void => onFilterChange(e, "state")}
        >
          <MenuItem value={"all"}>Összes státusz</MenuItem>
          {showedStates.map((state: DeviceState, i: Key): JSX.Element => {
            return (
              <MenuItem value={state.key} key={i}>
                {state.desc}
              </MenuItem>
            );
          })}
        </Select>
        <Select
          value={filter.category.toString()}
          style={style.filterTextField}
          onChange={(e: SelectChangeEvent<string>): void => onFilterChange(e, "category")}
        >
          <MenuItem value={"all"}>Összes eszköz kategória</MenuItem>
          {categories.map((category: DeviceCategoryModel, i: Key): JSX.Element => {
            return (
              <MenuItem value={category.id} key={i}>
                {category.name}
              </MenuItem>
            );
          })}
        </Select>
        <Select
          value={filter.type.toString()}
          style={style.filterTextField}
          onChange={(e: SelectChangeEvent<string>): void => onFilterChange(e, "type")}
        >
          <MenuItem value={"all"}>Összes eszköz típus</MenuItem>
          {filteredTypes.map((type: DeviceTypeModel, i: Key): JSX.Element => {
            return (
              <MenuItem value={type.id} key={i}>
                {type.name}
              </MenuItem>
            );
          })}
        </Select>
      </Container>

      {/* Table */}
      <TableContainer style={style.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              {headMemo}
              <TableCell style={style.tableHeadCell} />
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredDevices
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((device: DeviceModel, i: Key): JSX.Element => {
                return (
                  <React.Fragment key={i}>
                    {/* Base data */}
                    <TableRow onClick={accordionChange(device.id.toString())} style={style.dataRow}>
                      {tableKeys.map((tableKey: DeviceTableKey, j: Key): JSX.Element => {
                        return (
                          <TableCell align={tableKey.align ?? "left"} style={style.dataCell} key={j}>
                            {(device as Record<string, unknown>)[tableKey.key] as string}
                          </TableCell>
                        );
                      })}
                      <TableCell align={"center"} style={style.dataCell}>
                        {device.state === "K" && (
                          <>
                            <Button
                              onClick={(e: React.MouseEvent<HTMLButtonElement>): void => {
                                e.stopPropagation();
                                // setReturningDevice(device);
                                // setReturningOpen(true);
                                onTakeBack(device);
                              }}
                            >
                              Visszavétel
                            </Button>
                            <Button
                              color="warning"
                              onClick={(e: React.MouseEvent<HTMLButtonElement>): void => {
                                e.stopPropagation();
                                void onReplacement(device);
                              }}
                            >
                              Csereigény
                            </Button>
                          </>
                        )}
                      </TableCell>
                    </TableRow>

                    {/* Additional data */}
                    <TableRow>
                      <TableCell style={style.accordionCell} colSpan={tableKeys.length + 1}>
                        <Accordion expanded={expanded === device.id.toString()}>
                          <AccordionSummary />
                          <AccordionDetails style={style.accordionDetails}>
                            <Grid container spacing={4}>
                              {gridKeys.map((gridKey: DeviceGridKey, j: Key): JSX.Element => {
                                return (
                                  <Grid key={j} item md={gridKey.md ?? 3} sm={gridKey.sm ?? 6} xs={gridKey.xs ?? 6}>
                                    <Typography key={1} style={style.gridLabel} align={gridKey.align ?? "left"}>
                                      {gridKey.label}
                                    </Typography>
                                    <Typography key={2} align={gridKey.align ?? "left"}>
                                      {(device as Record<string, unknown>)[gridKey.key] as string}
                                    </Typography>
                                  </Grid>
                                );
                              })}
                            </Grid>
                          </AccordionDetails>
                        </Accordion>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100, 250]}
        component="div"
        count={filteredDevices.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* Return modal */}
      <Dialog
        TransitionComponent={Transition}
        maxWidth={false}
        PaperProps={{ style: { width: "100%" } }}
        open={isReturningOpen}
        onClose={(): void => setReturningOpen(false)}
      >
        <ReturnDevice returningDevice={returningDevice} setReturningOpen={setReturningOpen} getDevices={getDevices} />
      </Dialog>
    </Paper>
  );
};

export default Devices;
